import { useEffect, useMemo, useState } from 'react';
import { act } from '../lib/actions';
import { api } from '../lib/api';
import { loadDigital } from '../lib/realtime';
import { useNova } from '../lib/store';
import type { ChatMessage, ChatView, DigitalOps, DigitalQueue, EmailMessage, EmailView } from '../lib/types';
import { Drawer } from './admin/kit';
import { Bubble } from './ChatPane';
import { CHANNELS } from './ContextPanel';
import { Mail } from './EmailPane';
import { Button, duration, Elapsed, Icon, Panel, useNow } from './ui';

type Conversation = (ChatView | EmailView) & { channel: 'chat' | 'email' };

const waitingStates = (c: Conversation) => c.status === 'queued' || c.status === 'ringing';

/** Everything live, oldest first: waiting ones first, then being worked on, then in wrap-up. */
function useConversations(d: DigitalOps | null): Conversation[] {
  return useMemo(() => {
    if (!d) return [];
    const all = [...Object.values(d.chats), ...Object.values(d.emails)] as Conversation[];
    const rank = (c: Conversation) => (waitingStates(c) ? 0 : c.status === 'connected' ? 1 : 2);
    return all.sort((a, b) => rank(a) - rank(b) || (a.queuedAt ?? a.startedAt) - (b.queuedAt ?? b.startedAt));
  }, [d]);
}

/** How many chats and emails each agent is working, for their tile. */
export function useDigitalLoad(): Record<string, { chats: number; emails: number; maxChats: number; maxEmails: number; qa30?: number | null }> {
  const d = useNova((s) => s.digital);
  return useMemo(() => {
    const out: Record<string, { chats: number; emails: number; maxChats: number; maxEmails: number; qa30?: number | null }> = {};
    if (!d) return out;
    for (const a of d.agents) out[a.userId] = { chats: 0, emails: 0, maxChats: a.maxChats, maxEmails: a.maxEmails, qa30: a.qa30 };
    for (const c of Object.values(d.chats)) if (c.agentId && out[c.agentId] && (c.status === 'connected' || c.status === 'ringing')) out[c.agentId].chats++;
    for (const m of Object.values(d.emails)) if (m.agentId && out[m.agentId] && m.status === 'connected') out[m.agentId].emails++;
    return out;
  }, [d]);
}

/**
 * Livechat and email queues on Live operations: what is waiting and for how
 * long, what is being worked on, and today's totals. Live from events; the
 * day's totals refresh every half minute.
 */
export function DigitalQueues() {
  const d = useNova((s) => s.digital);
  const now = useNow();
  useEffect(() => {
    void loadDigital();
    const t = setInterval(() => void loadDigital(), 30_000);
    return () => clearInterval(t);
  }, []);
  const list = useConversations(d);
  if (!d) return null;
  return (
    <Panel className="p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-medium">Livechat and email</h2>
        <p className="text-xs text-ink-3">Live; today's totals update every half minute</p>
      </div>
      {d.queues.length === 0 ? (
        <p className="text-sm text-ink-2">
          No livechat or email queues yet. Add one under Admin &gt; Queues with channel Livechat or Email, then point a livechat widget (Admin &gt;
          Channels &gt; Livechat) or a mailbox (Admin &gt; Channels &gt; Email) at it. Its chats and emails then show here as they happen.
        </p>
      ) : null}
      <ul className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3" aria-label="Livechat and email queues">
        {d.queues.map((q) => (
          <QueueCard key={q.queueId} q={q} list={list.filter((c) => c.queueId === q.queueId)} now={now} />
        ))}
      </ul>
    </Panel>
  );
}

function QueueCard({ q, list, now }: { q: DigitalQueue; list: Conversation[]; now: number }) {
  const ch = CHANNELS[q.channel];
  const waiting = list.filter(waitingStates);
  const working = list.filter((c) => c.status === 'connected');
  const oldest = waiting.reduce<number | null>((m, c) => Math.min(m ?? Infinity, c.queuedAt ?? c.startedAt), null);
  const waitS = oldest ? (now - oldest) / 1000 : 0;
  // Livechat is urgent after its queue's service level, a messaging app after 10 minutes, email after an hour
  const late = waiting.length > 0 && waitS > (q.channel === 'chat' ? q.slaSeconds : q.channel === 'email' ? 3600 : 600);
  return (
    <li className={`rounded-lg border p-3 ${late ? 'border-break/60' : 'border-line'}`} aria-label={`${q.name} queue`}>
      <div className="mb-2 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-md text-white" style={{ background: ch.color }}>
          <Icon name={ch.icon} size={14} />
        </span>
        <span className="min-w-0 flex-1 truncate font-medium">{q.name}</span>
        <span className="text-[11px] text-ink-3">{ch.label}</span>
      </div>
      <dl className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Waiting" value={String(waiting.length)} alert={late} />
        <Stat label="Longest wait" value={oldest ? duration(waitS) : '0:00'} alert={late} />
        <Stat label={q.channel === 'email' ? 'With agents' : 'Chatting'} value={String(working.length)} />
        <Stat label="Today" value={String(q.receivedToday)} />
        <Stat label="Handled" value={String(q.handledToday)} />
        <Stat label="First reply" value={q.avgFirstResponseToday === null ? '–' : duration(q.avgFirstResponseToday)} />
      </dl>
    </li>
  );
}

function Stat({ label, value, alert = false }: { label: string; value: string; alert?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] text-ink-3">{label}</dt>
      <dd className={`tabular text-lg font-semibold ${alert ? 'text-break' : ''}`}>{value}</dd>
    </div>
  );
}

/** Every chat and email waiting or being worked on; open one to watch it live. */
export function LiveConversations() {
  const d = useNova((s) => s.digital);
  const list = useConversations(d);
  const [watching, setWatching] = useState<string | null>(null);
  if (!d) return null;
  const current = watching ? (d.chats[watching] ?? d.emails[watching]) : null;
  return (
    <Panel className="p-5">
      <h2 className="mb-3 text-base font-medium">Live chats and emails</h2>
      {list.length === 0 ? (
        <p className="text-sm text-ink-2">No chats or emails waiting or being worked on.</p>
      ) : (
        <ul className="flex max-h-[420px] flex-col gap-2 overflow-auto pr-1" aria-label="Live chats and emails">
          {list.map((c) => (
            <ConversationRow key={c.interactionId} c={c} onWatch={() => setWatching(c.interactionId)} />
          ))}
        </ul>
      )}
      {watching ? <Watch id={watching} c={current as Conversation | null} onClose={() => setWatching(null)} /> : null}
    </Panel>
  );
}

function statusOf(c: Conversation): string {
  if (c.status === 'queued') return 'Waiting';
  if (c.status === 'ringing') return `Offered to ${c.agentName?.split(' ')[0] ?? 'an agent'}`;
  if (c.status === 'wrap_up') return `${c.agentName?.split(' ')[0] ?? 'Agent'} wrapping up`;
  if (c.channel === 'email') return `${c.agentName ?? 'An agent'}${(c as EmailView).replied ? ', replied' : ''}`;
  return `With ${c.agentName ?? 'an agent'}`;
}

function ConversationRow({ c, onWatch }: { c: Conversation; onWatch: () => void }) {
  const ch = CHANNELS[c.channel];
  const since = c.status === 'connected' ? (c.answeredAt ?? c.startedAt) : c.status === 'wrap_up' ? (c.endedAt ?? c.startedAt) : (c.queuedAt ?? c.startedAt);
  return (
    <li className="flex items-center gap-3 rounded-lg border border-line px-3 py-2">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-white" style={{ background: ch.color }} title={ch.label}>
        <Icon name={ch.icon} size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">
          {c.customerName ?? c.customerEmail ?? 'Visitor'}
          {c.channel === 'email' ? <span className="font-normal text-ink-2"> · {(c as EmailView).subject}</span> : null}
        </span>
        <span className={`block truncate text-[12px] ${waitingStates(c) ? 'text-break' : 'text-ink-3'}`}>
          {statusOf(c)} · {c.queueName ?? 'No queue'} · <Elapsed since={since} />
        </span>
      </span>
      <Button tone="quiet" className="px-2.5 py-1 text-xs" onClick={onWatch} aria-label={`Watch ${c.customerName ?? 'conversation'}`}>
        {c.channel === 'email' ? 'Open' : 'Watch'}
      </Button>
    </li>
  );
}

/**
 * A supervisor watching a chat as it happens (new messages arrive as
 * events), or reading an email conversation. A chat can be ended from here.
 */
function Watch({ id, c, onClose }: { id: string; c: Conversation | null; onClose: () => void }) {
  const chatMessages = useNova((s) => s.chatMessages[id]);
  const emailMessages = useNova((s) => s.emailMessages[id]);
  const [channel] = useState(c?.channel === 'email' ? 'email' : 'chat');
  useEffect(() => {
    let live = true;
    if (channel === 'chat') {
      api<ChatMessage[]>('GET', `/chats/${id}/messages`)
        .then((m) => live && useNova.getState().set({ chatMessages: { ...useNova.getState().chatMessages, [id]: m } }))
        .catch(() => undefined);
    } else {
      api<EmailMessage[]>('GET', `/emails/${id}/messages`)
        .then((m) => live && useNova.getState().set({ emailMessages: { ...useNova.getState().emailMessages, [id]: m } }))
        .catch(() => undefined);
    }
    return () => {
      live = false;
    };
  }, [id, channel]);
  const title = c ? `${channel === 'chat' ? 'Chat' : 'Email'} with ${c.customerName ?? c.customerEmail ?? 'a visitor'}` : 'Conversation';
  return (
    <Drawer
      title={title}
      open
      onClose={onClose}
      footer={
        <>
          {c && channel === 'chat' && c.status !== 'wrap_up' ? (
            <Button tone="danger" className="px-3 py-2" onClick={() => void act(() => api('POST', `/chats/${id}/end`).then(() => true), 'Chat ended')}>
              End chat
            </Button>
          ) : null}
          <Button tone="ghost" className="ml-auto px-3 py-2" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <p className="mb-3 text-[12px] text-ink-3">
        {c ? `${statusOf(c)} · ${c.queueName ?? 'No queue'}` : 'This conversation is finished.'}
        {channel === 'chat' && c?.status === 'connected' ? ' · new messages appear as they are sent' : ''}
      </p>
      <div aria-label="Conversation" aria-live="polite">
        {channel === 'chat' ? (
          chatMessages ? (
            chatMessages.map((m) => <Bubble key={m.id} m={m} />)
          ) : (
            <p className="text-sm text-ink-3">Loading…</p>
          )
        ) : emailMessages ? (
          emailMessages.map((m, n) => <Mail key={m.id} m={m} earlier={m.interactionId !== id} last={n === emailMessages.length - 1} />)
        ) : (
          <p className="text-sm text-ink-3">Loading…</p>
        )}
      </div>
    </Drawer>
  );
}
